import React from "react";
import { GridColDef, GridRenderCellParams, GridValueGetter } from "@mui/x-data-grid";

import { Box, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import Icon from "../../components/ui/Icon";
import { UserRole } from "../../model/UserRole";
import {
  getsylobProduct,
  getTranslationStatusColor,
  transformQuarterCodes,
} from "../../utils/ApiUtils/TranslationHelper";
import { getTranslationStatusOptions, Translation, TranslationStatus } from "../../model/Translation";
import { TranslationMode } from "../../api/fetchTranslationLabels";
import TranslationProgressBarSmall from "../../components/translation/progress/TranslationPhasesProgressBar";

type HandleViewTranslation = (translation: Translation, mode: TranslationMode) => void;
type HandleEditTranslation = (translation: Translation, mode: TranslationMode) => void;
type HandleAcceptTranslationReview = (translationId: number) => void;
type HandleRefuseTranslationReview = (translationId: number) => void;

export const getTranslationPhaseHiddenColumns = (): string[] => [
  "createdAt",
  "reviewDate",
  "translationDate",
  "translationEndDate",
];
export const getTranslationPhaseColumns = (
  handleViewTranslation: HandleViewTranslation,
  handleEditTranslation: HandleEditTranslation,
  handleAcceptTranslationReview: HandleAcceptTranslationReview,
  handleRefuseTranslationReview: HandleRefuseTranslationReview,
  roles: UserRole[]
): GridColDef[] => [
  {
    field: "product",
    headerName: "Product",
    width: 72,
    hideable: false,
    valueGetter: (value) => getsylobProduct(value),
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const productName = getsylobProduct(params.row.product);
      return (
        <Tooltip title={productName}>
          <span> {productName}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "destinationLanguage",
    headerName: "Language",
    hideable: false,
    width: 110,
    valueGetter: (value, row: Translation) => row.destinationLanguageName,
    renderCell: (params: GridRenderCellParams<Translation>) => {
      return (
        <Tooltip title={params.row.destinationLanguage}>
          <span> {params.row.destinationLanguageName}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "version",
    headerName: "Major version",
    hideable: false,
    valueGetter: (value) => transformQuarterCodes(value),
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const productVersion = transformQuarterCodes(params.row.version);
      return (
        <Tooltip title={params.row.version}>
          <span> {productVersion}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "mavenVersion",
    headerName: "Version",
    hideable: false,
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const mavenVersion = params.row.mavenVersion.replace("-SNAPSHOT", "");
      return (
        <Tooltip title={params.row.mavenVersion}>
          <span> {mavenVersion}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 150,
    hideable: false,
    type: "singleSelect",
    valueOptions: getTranslationStatusOptions(),
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const { value } = params;
      const textColor = getTranslationStatusColor(value);
      const error = params.row.error;
      return (
        <div className="flex items-center gap-1">
          <Tooltip title={value}>
            <span className={`font-display font-bold ${textColor}`}>{value}</span>
          </Tooltip>

          {error && (
            <Tooltip title={error.errorMessage} enterTouchDelay={0} leaveTouchDelay={5000}>
              <span className="inline-flex items-center text-red-500 cursor-pointer">
                <Icon icon="triangleExclamation" />
              </span>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    field: "labelscount",
    headerName: "Progress",
    width: 200,
    hideable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const { initialLabelsToTranslate, nbLabelsToTranslate } = params.row;
      const translated = initialLabelsToTranslate - nbLabelsToTranslate;
      const progress = (translated / (initialLabelsToTranslate || 1)) * 100;
      return (
        <TranslationProgressBarSmall
          total={initialLabelsToTranslate}
          remaining={nbLabelsToTranslate}
          progress={progress}
          totalLabel={"To Translate"}
          remainingLabel={"Left"}
        />
      );
    },
  },
  {
    field: "dueDate",
    headerName: "Due date",
    width: 180,
  },
  {
    field: "createdAt",
    headerName: "Extraction date",
  },
  {
    field: "translationDate",
    headerName: "Translation start date",
  },
  {
    field: "reviewDate",
    headerName: "Review date",
  },
  {
    field: "translationEndDate",
    headerName: "Translation end date",
  },
  {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    filterable: false,
    hideable: false,
    width: 180,
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const { row } = params;
      const allowReview = row.status !== TranslationStatus.IN_REVIEW;
      const allowTranslate =
        row.status === TranslationStatus.EXTRACTED ||
        row.status === TranslationStatus.IN_TRANSLATION ||
        row.status === TranslationStatus.REVIEW_REJECTED;
      const allowValidateOrRefuse = row.status === TranslationStatus.IN_REVIEW;

      return (
        <Box sx={{ display: "flex" }}>
          {(roles.includes(UserRole.CONTROLLER) || roles.includes(UserRole.ADMIN)) && (
            <Tooltip title={allowReview ? "Cannot review translation" : "Review translation"}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => row.id && handleViewTranslation(row, "review")}
                  disabled={allowReview}
                  color="secondary"
                >
                  <Icon icon="view" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={allowTranslate ? "tap to start editing translation" : "Cannot edit this translation"}>
            <span>
              <IconButton
                size="small"
                disabled={!allowTranslate}
                onClick={() => row.id && handleEditTranslation(row, "edit")}
                color="primary"
              >
                <Icon icon="modify" />
              </IconButton>
            </span>
          </Tooltip>
          {(roles.includes(UserRole.CONTROLLER) || roles.includes(UserRole.ADMIN)) && (
            <Tooltip title={allowValidateOrRefuse ? "Validate the translation" : "Cannot validate the translation"}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleAcceptTranslationReview(row.id)}
                  disabled={!allowValidateOrRefuse}
                  color="success"
                >
                  <Icon icon="check" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {(roles.includes(UserRole.CONTROLLER) || roles.includes(UserRole.ADMIN)) && (
            <Tooltip title={allowValidateOrRefuse ? "Refuse the translation" : "Cannot refuse the translation"}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleRefuseTranslationReview(row.id)}
                  disabled={!allowValidateOrRefuse}
                  color="error"
                >
                  <Icon icon="uncheck" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    },
  },
];
